import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {checklistShape} from '../shapes';
import ChecklistItem from './ChecklistItem';

function SortableItem({item, checklistId, onToggle, onDelete, onUpdate, onStar, onSetDueDate}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id});
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="checklist-item-wrapper" {...listeners} {...attributes}>
            <ChecklistItem
                checklistId={checklistId}
                item={item}
                onToggle={onToggle}
                onDelete={onDelete}
                onUpdate={onUpdate}
                onStar={onStar}
                onSetDueDate={onSetDueDate}
            />
        </div>
    );
}

const Checklist = memo(function Checklist({data, onDelete, onAddItem, onToggleItem, onDeleteItem, onUpdateTitle, onUpdateItem, onReorderItems, onToggleSortMode, onStarItem, onSetDueDateItem, readOnly, isFocused, onFocus, onUnfocus, dragListeners, isDragEnabled}) {
    const [newItemTitle, setNewItemTitle] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({top: 0, right: 0});
    const menuRef = useRef(null);
    const menuBtnRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        function handleMouseDown(e) {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                menuBtnRef.current && !menuBtnRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [menuOpen]);

    function openMenu() {
        if (menuBtnRef.current) {
            const rect = menuBtnRef.current.getBoundingClientRect();
            setDropdownPos({top: rect.bottom + 4, right: window.innerWidth - rect.right});
        }
        setMenuOpen(o => !o);
    }

    const isCustomSort = data.sortMode === 'custom';

    const displayedItems = useMemo(() => {
        if (isCustomSort) return data.items;
        return [...data.items].sort((a, b) => a.position - b.position);
    }, [data.items, isCustomSort]);

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = displayedItems.findIndex(i => i.id === active.id);
        const newIndex = displayedItems.findIndex(i => i.id === over.id);
        const newItems = arrayMove(displayedItems, oldIndex, newIndex);
        const afterId = newIndex > 0 ? newItems[newIndex - 1].id : null;
        onReorderItems(data.id, active.id, afterId, newItems);
    }

    function handleAdd() {
        const title = newItemTitle.trim();
        if (!title) return;
        onAddItem(data.id, title);
        setNewItemTitle('');
    }

    function startEditingTitle() {
        setTitleDraft(data.title);
        setEditingTitle(true);
    }

    function commitTitle() {
        const title = titleDraft.trim();
        if (title && title !== data.title) onUpdateTitle(data.id, title);
        setEditingTitle(false);
    }

    function handleTitleKeyDown(e) {
        if (e.key === 'Enter') commitTitle();
        if (e.key === 'Escape') setEditingTitle(false);
    }

    const checked = data.items.filter(i => i.checked).length;
    const total = data.items.length;
    const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

    return (
        <div className={`checklist${isFocused ? ' checklist--focused' : ''}`}>
            <div className="checklist-sticky">
            <div className="checklist-header">
                {isDragEnabled && (
                    <span className="checklist-drag-handle" {...(dragListeners || {})} aria-label="Drag to reorder checklist">
                        <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="3" cy="2.5" r="1.2" fill="currentColor"/>
                            <circle cx="7" cy="2.5" r="1.2" fill="currentColor"/>
                            <circle cx="3" cy="7" r="1.2" fill="currentColor"/>
                            <circle cx="7" cy="7" r="1.2" fill="currentColor"/>
                            <circle cx="3" cy="11.5" r="1.2" fill="currentColor"/>
                            <circle cx="7" cy="11.5" r="1.2" fill="currentColor"/>
                        </svg>
                    </span>
                )}
                {editingTitle
                    ? <input
                        className="checklist-title-input"
                        value={titleDraft}
                        onChange={e => setTitleDraft(e.target.value)}
                        onBlur={commitTitle}
                        onKeyDown={handleTitleKeyDown}
                        autoFocus
                      />
                    : <h2
                        onClick={startEditingTitle}
                        onKeyDown={e => e.key === 'Enter' && startEditingTitle()}
                        tabIndex={0}
                      >{data.title}</h2>
                }
                <div className="checklist-header-actions">

                    <button
                        className="checklist-focus-btn"
                        onClick={() => isFocused ? onUnfocus() : onFocus(data.id)}
                        aria-label={isFocused ? 'Exit focus mode' : 'Focus checklist'}
                    >
                        {isFocused
                            ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="4,1 1,1 1,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="8,1 11,1 11,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="11,8 11,11 8,11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="4,11 1,11 1,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            : <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="1,4 1,1 4,1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="11,4 11,1 8,1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="8,11 11,11 11,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="1,8 1,11 4,11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                        }
                    </button>
                    <button
                        ref={menuBtnRef}
                        className="checklist-header-menu-btn"
                        onClick={openMenu}
                        aria-label="Checklist settings"
                        aria-expanded={menuOpen}
                    >
                        <svg width="3" height="12" viewBox="0 0 3 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="1.5" cy="2" r="1.4" fill="currentColor"/>
                            <circle cx="1.5" cy="6" r="1.4" fill="currentColor"/>
                            <circle cx="1.5" cy="10" r="1.4" fill="currentColor"/>
                        </svg>
                    </button>
                </div>
                {total > 0 && (
                    <div className="checklist-header-progress">
                        <div className="checklist-header-progress-fill" style={{width: `${pct}%`}} />
                    </div>
                )}
            </div>
            {!readOnly && (
                <div className="checklist-add-item">
                    <div className="checklist-add-item-inner">
                        <input
                            type="text"
                            placeholder="Add new item"
                            value={newItemTitle}
                            onChange={e => setNewItemTitle(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        />
                        <button onClick={handleAdd}>Add</button>
                    </div>
                </div>
            )}
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={displayedItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {displayedItems.map(item => (
                        <SortableItem
                            key={item.id}
                            item={item}
                            checklistId={data.id}
                            onToggle={onToggleItem}
                            onDelete={onDeleteItem}
                            onUpdate={onUpdateItem}
                            onStar={onStarItem}
                            onSetDueDate={onSetDueDateItem}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {menuOpen && createPortal(
                <div
                    ref={menuRef}
                    className="checklist-item-menu-dropdown"
                    role="menu"
                    style={{top: dropdownPos.top, right: dropdownPos.right}}
                >
                    <button
                        className="checklist-item-menu-item checklist-item-menu-item--danger"
                        onClick={() => { onDelete(data.id); setMenuOpen(false); }}
                        aria-label="Delete checklist"
                        role="menuitem"
                    >
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="1,3 11,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Delete
                    </button>
                </div>,
                document.querySelector('.App') || document.body
            )}
        </div>
    );
});

Checklist.propTypes = {
    data: checklistShape.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAddItem: PropTypes.func.isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onUpdateTitle: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
    onReorderItems: PropTypes.func.isRequired,
    onToggleSortMode: PropTypes.func.isRequired,
    onStarItem: PropTypes.func.isRequired,
    onSetDueDateItem: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    isFocused: PropTypes.bool,
    onFocus: PropTypes.func.isRequired,
    onUnfocus: PropTypes.func.isRequired,
    dragListeners: PropTypes.object,
    isDragEnabled: PropTypes.bool,
};

export default Checklist;
